import { useState, useEffect, useCallback, type ReactNode } from 'react';
import api from '../api/client';
import { ProjectContext } from './ProjectContext';
import { useAuth } from '../hooks/useAuth';
import type { Project } from '../types';

export function ProjectProvider({ children }: { children: ReactNode }) {
  const { token } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProjectState] = useState<Project | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshProjects = useCallback(async () => {
    if (!token) {
      setProjects([]);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const res = await api.get('/api/v1/projects?per_page=100');
      const list = res.data.data || [];
      setProjects(list);
      const savedId = localStorage.getItem('selectedProjectId');
      if (savedId) {
        const found = list.find((p: Project) => String(p.id) === savedId);
        if (found) setSelectedProjectState(found);
      } else if (list.length >= 1) {
        const activeProjects = list.filter((p: Project) => p.status === 'aktif');
        const defaultProject = activeProjects.length > 0
          ? activeProjects.sort((a: Project, b: Project) =>
              new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
            )[0]
          : list[0];
        setSelectedProjectState(defaultProject);
        localStorage.setItem('selectedProjectId', String(defaultProject.id));
      }
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string }; status?: number }; message?: string };
      console.error('ProjectProvider fetch error:', e.response?.data || e.message || err);
      setError(e.response?.data?.message || e.message || 'Projeler yüklenirken bir hata oluştu.');
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    let cancelled = false;
    refreshProjects().then(() => {
      if (cancelled) return;
    });
    return () => { cancelled = true; };
  }, [token, refreshProjects]);

  const setSelectedProject = (project: Project | null) => {
    setSelectedProjectState(project);
    if (project) {
      localStorage.setItem('selectedProjectId', String(project.id));
    } else {
      localStorage.removeItem('selectedProjectId');
    }
  };

  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === 'selectedProjectId') {
        const id = e.newValue;
        if (id) {
          const found = projects.find((p) => String(p.id) === id);
          if (found) setSelectedProjectState(found);
        } else {
          setSelectedProjectState(null);
        }
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [projects]);

  return (
    <ProjectContext.Provider value={{ selectedProject, setSelectedProject, projects, isLoading, error, refreshProjects }}>
      {children}
    </ProjectContext.Provider>
  );
}
